import { Component, Input, OnDestroy, OnInit, Output, ViewChild, EventEmitter } from '@angular/core';
import Emitter from 'quill/core/emitter';
import { ProjectService } from '../../shared/services/project.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { HelperService } from '../../shared/services/helper.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { CommentEditFocusEvent, CommentReplyFocusEvent } from '../../shared/models';
import Quill from 'quill';
import { Mention, MentionBlot } from 'quill-mention';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from '../../shared/services/timeline.service';
import { AnnotationPrivateCondition } from '../../shared/interfaces';
import { AnnotationService } from '../../shared/services/annotation.service';
import { saveSelection, restoreSelection } from '../../shared/utils/save-and-restore-selection';
import { StringUtilsService } from '../../shared/services/string-utils.service';
import { GeneralUtilsService } from '../../shared/services/general-utils.service';

Quill.register(
  {
    'blots/mention': MentionBlot,
    'modules/mention': Mention,
  },
  true
);

@Component({
  selector: 'mtm-text-editor',
  templateUrl: './mtm-text-editor.component.html',
  styleUrl: './mtm-text-editor.component.scss',
  standalone: false,
})
export class MtmTextEditorComponent implements OnInit, OnDestroy {
  @ViewChild('editor', { static: false }) editor: any;
  @ViewChild('mtmUploadFileExplorer', { static: true }) mtmUploadFileExplorer: any;

  @Input() placeholder: string = 'writeComment';
  @Input() participants: any = [];
  @Input() elementId: string = this.guid();
  @Input() expandable: boolean = false;
  @Input() userAvatar: any;
  @Input() relatedCommentId: string = ''; //tie editor to comment
  @Input() showBottomToolbar: boolean = false;
  @Input() hideQuillToolbar: boolean = false;
  @Input() files: any = [];
  @Input() videoRecordFiles: any = [];
  @Input() audioRecordFiles: any = [];
  @Input() supportPrivateComment: boolean = false;
  @Input() privateCondition: AnnotationPrivateCondition | null = null;
  @Input() enableEnterSubmit: boolean = false;
  @Output() privateConditionChange: EventEmitter<AnnotationPrivateCondition> =
    new EventEmitter<AnnotationPrivateCondition>();
  @Output() attachmentsChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() uploadFileCallback: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteFileCallback: EventEmitter<any> = new EventEmitter<any>();
  @Output() enterKeyPress: EventEmitter<void> = new EventEmitter<void>();

  attachmentsValue: any;

  @Input()
  get attachments(): any {
    return this.attachmentsValue;
  }

  set attachments(val: any) {
    this.attachmentsValue = val;
    this.attachmentsChange.emit(this.attachmentsValue);
  }

  isEmojiPickerVisible: boolean = false;
  modelValue: any;

  @Input()
  get value(): any {
    return this.modelValue;
  }

  set value(val: any) {
    this.modelValue = val;
    this.valueChange.emit(this.modelValue);
  }

  quillModule: any;
  readonly quillCustomModules = [
    { path: 'blots/mention', implementation: MentionBlot },
    { path: 'modules/mention', implementation: Mention },
  ];
  quillEditorInstance: any;
  private enterKeyHandler: ((event: KeyboardEvent) => void) | null = null;
  private editorElementForCleanup: HTMLElement | null = null;
  private readonly enterKeySubject: Subject<void> = new Subject<void>();
  private isSubmitting: boolean = false;

  ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly projectService: ProjectService,
    private readonly generalUtilsService: GeneralUtilsService,
    private readonly stringUtilsService: StringUtilsService,
    private readonly modalService: NgbModal,
    private readonly annotationService: AnnotationService
  ) {}

  ngOnInit(): void {
    this.initQuillModule();
    EmitterService.get(CommentReplyFocusEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: any) => {
        const { parentId: parentCommentId } = data;
        if (parentCommentId === this.relatedCommentId) {
          setTimeout(() => {
            this.editor.elementRef.nativeElement.focus();
          }, 0);
        }
      });
    EmitterService.get(CommentEditFocusEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: any) => {
        const { commentId } = data;
        if (commentId === this.relatedCommentId) {
          setTimeout(() => {
            this.editor.elementRef.nativeElement.focus();
          }, 0);
        }
      });

    this.annotationService.privateConditionChange$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((privateCondition: AnnotationPrivateCondition) => {
        if (this.privateCondition != privateCondition) {
          this.privateCondition = privateCondition;
        }
      });

    if (this.enableEnterSubmit) {
      this.enterKeySubject.pipe(debounceTime(500), takeUntil(this.ngUnsubscribe)).subscribe(() => {
        if (!this.isSubmitting) {
          this.isSubmitting = true;
          this.enterKeyPress.emit();
          setTimeout(() => {
            this.isSubmitting = false;
          }, 1000);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.enableEnterSubmit && this.enterKeyHandler && this.editorElementForCleanup) {
      this.editorElementForCleanup.removeEventListener('keydown', this.enterKeyHandler, true);
      this.enterKeyHandler = null;
      this.editorElementForCleanup = null;
    }
    this.enterKeySubject.complete();
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private setPrivateCondition(value: AnnotationPrivateCondition): void {
    this.privateCondition = value;
    this.privateConditionChange.emit(value);
  }

  private getSelectedText(): any {
    let txt: any = null;
    if (globalThis.getSelection) {
      txt = globalThis.getSelection();
    } else if (globalThis.document?.getSelection) {
      txt = globalThis.document.getSelection();
    } else if ((globalThis.document as any)?.selection) {
      txt = (globalThis.document as any).selection.createRange().text;
    }
    return txt;
  }

  private initQuillModule(): void {
    this.quillModule = {
      toolbar: this.hideQuillToolbar
        ? false
        : {
            container: [
              ['bold', 'italic', 'underline'],
              ['link'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['blockquote', 'code-block'],
              ['emoji'],
            ],
            handlers: {
              link: (value: any): void => {
                if (value) {
                  const editorInstance: any = this.quillEditorInstance;
                  const range: any = editorInstance.selection;
                  if (range.savedRange == null || range.savedRange.length === 0) return;
                  const preview: any = this.getSelectedText();
                  const { tooltip }: any = editorInstance.theme;
                  tooltip.save = function (): void {
                    let val: any = tooltip.textbox.value;
                    if (!val.includes('https') && !val.includes('http')) {
                      val = 'https://' + val;
                    }
                    this.quill.format('link', val, Emitter.sources.USER);
                  };
                  tooltip.edit('link', preview);
                }
              },
              emoji: (): void => {
                this.isEmojiPickerVisible = !this.isEmojiPickerVisible;
              },
            },
          },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@', '#'],
        source: (searchTerm: string, renderList: any, mentionChar: string): void => {
          let values: any;
          const participants: any =
            this.participants && this.participants.length > 0
              ? this.participants
              : this.projectService.project?.participants || [];
          if (mentionChar === '@') {
            values = participants
              .map((participant: any) => ({
                id: participant.username,
                value: participant.fullName,
                type: 'one_on_one',
                denotationChar: mentionChar,
              }))
              .filter((data: any) => !this.generalUtilsService.isObjectEmpty(data.value.trim()));
          } else {
            values = participants
              .filter((conversation: any) => conversation.type !== 'ONE_TO_ONE')
              .map((conversation: any) => ({
                id: conversation.id,
                value: conversation.title,
                type: 'group',
                users: conversation.users,
                denotationChar: mentionChar,
              }));
          }

          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
          } else {
            const matches: any[] = [];
            for (const value of values) {
              if (
                ~this.stringUtilsService
                  .getNormalizedName(value.value)
                  .toLowerCase()
                  .indexOf(this.stringUtilsService.getNormalizedName(searchTerm).toLowerCase())
              ) {
                matches.push(value);
              }
            }
            renderList(matches, searchTerm);
          }
        },
      },
    };
  }

  private injectCommentVisibilityDropdown(): void {
    const select: any = document.createElement('select');
    const publicOption: any = new Option('Public', '');
    publicOption.setAttribute('title', 'All project members');
    select.appendChild(publicOption);
    const domainOption: any = new Option('Private', 'DOMAIN');
    domainOption.setAttribute('title', 'Company members only');
    select.appendChild(domainOption);
    select.classList.add('d-inline-block', 'comment-visibility');

    switch (this.privateCondition) {
      case 'COMPANY':
      case 'DOMAIN':
      case 'ALL':
        select.value = this.privateCondition;
        break;
      default:
        select.value = '';
        break;
    }

    const selectRef: any = select;
    selectRef.toolbar = this.quillEditorInstance.getModule('toolbar');
    selectRef.toolbarEl = selectRef.toolbar.container;
    selectRef.toolbarEl.insertBefore(select, selectRef.toolbarEl.firstChild);

    select.addEventListener('change', () => {
      const value: any = select.value.toString();
      if (value == this.privateCondition) {
        return;
      }
      if (value) {
        this.setPrivateCondition(<AnnotationPrivateCondition>value);
      } else {
        this.setPrivateCondition(null);
      }
    });

    select.addEventListener('click', (event: any) => {
      event.stopPropagation();
    });
  }

  private guid(): string {
    const s4: () => string = () => {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    };
    return `${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  public onEditorInitialized(editor: Quill): void {
    this.quillEditorInstance = editor;

    if (this.supportPrivateComment) {
      this.injectCommentVisibilityDropdown();
    }

    const toolbar: any = editor.getModule('toolbar');
    if (toolbar) {
      const emojiButton: any = toolbar.container.querySelector('.ql-emoji');
      if (emojiButton) {
        emojiButton.setAttribute('title', 'Insert Emoji');
        emojiButton.style.cursor = 'pointer';
      }
    }

    if (this.enableEnterSubmit) {
      const editorElement: HTMLElement = editor.root;
      const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
          const editorLength: number = editor.getLength();
          const hasText: boolean = editor.getText().trim().length > 0;
          const editorContent: HTMLElement = editor.root;
          const hasMentions: boolean =
            editorContent.querySelectorAll('span.mention, span[data-type="mention"], span[data-denotation-char]')
              .length > 0;
          const hasContent: boolean = editorLength > 1 || hasText || hasMentions;

          if (hasContent && !this.isSubmitting) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            this.enterKeySubject.next();
          }
        }
      };
      editorElement.addEventListener('keydown', handleKeyDown, true);
      this.enterKeyHandler = handleKeyDown;
      this.editorElementForCleanup = editorElement;
    }
  }

  public textChanged($event: any): void {
    if ($event.editor.getLength() >= 1024) {
      $event.editor.deleteText(1024, $event.editor.getLength());
    }
  }

  public addEmoji($event: any): void {
    if (this.quillEditorInstance) {
      const range: any = this.quillEditorInstance.getSelection(true);
      const cursorPos: any = range ? range.index : this.quillEditorInstance.getLength();
      this.quillEditorInstance.insertText(cursorPos, $event.emoji.native, 'user');
      this.quillEditorInstance.setSelection(cursorPos + $event.emoji.native.length);
      this.value = this.quillEditorInstance.root.innerHTML;
    }
    this.isEmojiPickerVisible = false;
  }

  public openFileExplorer(): void {
    const element: any = this.mtmUploadFileExplorer.fileInput.nativeElement;
    element.click();
  }

  public fileChangeListener(file: any): void {
    this.uploadFileCallback.emit(file);
  }

  public deleteFile(file: any): void {
    this.deleteFileCallback.emit(file);
  }
}
